
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface BannerSettings {
  isEnabled: boolean;
  title: string;
  subtitle: string;
  buttonText: string;
  buttonUrl: string;
  bgColor: string;
  textColor: string;
  startDate: string;
  endDate: string;
  position: 'top' | 'middle';
}

interface PromoBannerProps {
  position: 'top' | 'middle';
}

export default function PromoBanner({ position }: PromoBannerProps) {
  const navigate = useNavigate();
  const [banner, setBanner] = useState<BannerSettings | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem('home_banner_settings');
    if (!raw) return;
    const parsed: BannerSettings = JSON.parse(raw);
    if (!parsed.isEnabled) return;

    const now = new Date();
    if (parsed.startDate && new Date(parsed.startDate) > now) return;
    if (parsed.endDate && new Date(parsed.endDate) < now) return;
    if (parsed.position !== position) return;

    setBanner(parsed);
  }, [position]);

  if (!banner || dismissed) return null;

  const handleClick = () => {
    if (banner.buttonUrl) {
      if (banner.buttonUrl.startsWith('http')) {
        window.open(banner.buttonUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(banner.buttonUrl);
      }
    }
  };

  return (
    <section className="py-4">
      <div className="max-w-6xl mx-auto px-4">
        <div
          className="relative rounded-2xl p-8 text-center overflow-hidden"
          style={{ backgroundColor: banner.bgColor, color: banner.textColor }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-32 h-32 rounded-full opacity-10 -translate-x-1/2 -translate-y-1/2" style={{ backgroundColor: banner.textColor }}></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full opacity-10 translate-x-1/3 translate-y-1/3" style={{ backgroundColor: banner.textColor }}></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full opacity-5" style={{ backgroundColor: banner.textColor }}></div>

          {/* Close button */}
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
            style={{ color: banner.textColor }}
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-2">{banner.title}</h3>
            {banner.subtitle && (
              <p className="text-base opacity-90 mb-5 max-w-xl mx-auto">{banner.subtitle}</p>
            )}
            {banner.buttonText && (
              <button
                onClick={handleClick}
                className="inline-flex items-center space-x-2 px-7 py-3 rounded-full text-sm font-bold transition-transform hover:scale-105 cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: banner.textColor,
                  color: banner.bgColor,
                }}
              >
                <span>{banner.buttonText}</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>

          {banner.endDate && (
            <div className="relative z-10 mt-4 text-xs opacity-70">
              Акция до {new Date(banner.endDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
